import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Badge } from '@/components/ui/badge';
import { Edit, Loader2, Phone, Mail, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { eventService } from '@/services/eventService';
import { EventProvider, EventTask } from '@/types';
import { ProviderMultiSelect } from './ProviderMultiSelect';

interface TaskDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: EventTask | null;
  /** Event-providers (`evento_proveedores`) assigned to this event. */
  eventProviders: EventProvider[];
  canWrite: boolean;
  /** Fired after a successful save/delete so the caller can refresh. */
  onMutated?: () => void;
}

const formatDateRange = (inicio: Date, fin: Date) => {
  const sameDay = inicio.toDateString() === fin.toDateString();
  const dateFmt: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  };
  const timeFmt: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  if (sameDay) {
    return `${inicio.toLocaleDateString('es-MX', dateFmt)} · ${inicio.toLocaleTimeString(
      'es-MX',
      timeFmt,
    )} – ${fin.toLocaleTimeString('es-MX', timeFmt)}`;
  }
  return `${inicio.toLocaleString('es-MX', { ...dateFmt, ...timeFmt })} – ${fin.toLocaleString(
    'es-MX',
    { ...dateFmt, ...timeFmt },
  )}`;
};

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  onOpenChange,
  task,
  eventProviders,
  canWrite,
  onMutated,
}) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [titulo, setTitulo] = useState('');
  const [fechaInicio, setFechaInicio] = useState<Date | undefined>(undefined);
  const [fechaFin, setFechaFin] = useState<Date | undefined>(undefined);
  const [notas, setNotas] = useState('');
  const [providerIds, setProviderIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Re-prime form state whenever a new task is loaded into the modal.
  useEffect(() => {
    if (task) {
      setTitulo(task.titulo);
      setFechaInicio(task.inicio);
      setFechaFin(task.fin);
      setNotas(task.notas ?? '');
      setProviderIds((task.providers ?? []).map((p) => p.evento_proveedor_id));
      setMode('view');
    }
  }, [task]);

  const handleClose = (next: boolean) => {
    if (!next) setMode('view');
    onOpenChange(next);
  };

  const handleSave = async () => {
    if (!task) return;
    if (!titulo.trim()) {
      toast({ title: 'El título es obligatorio', variant: 'destructive' });
      return;
    }
    if (!fechaInicio) {
      toast({ title: 'La fecha de inicio es obligatoria', variant: 'destructive' });
      return;
    }
    if (fechaFin && fechaFin < fechaInicio) {
      toast({ title: 'La fecha fin no puede ser anterior al inicio', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await eventService.updateEventTask(task.id, {
        titulo: titulo.trim(),
        inicio: fechaInicio,
        fin: fechaFin ?? fechaInicio,
        notas: notas.trim() || undefined,
      });
      await eventService.setTaskProviders(task.id, providerIds);
      toast({ title: 'Tarea actualizada' });
      setMode('view');
      onMutated?.();
    } catch (e) {
      console.error('Error updating task:', e);
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo actualizar la tarea',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!confirm(`¿Eliminar la tarea "${task.titulo}"?`)) return;
    setSubmitting(true);
    try {
      await eventService.deleteEventTask(task.id);
      toast({ title: 'Tarea eliminada' });
      onMutated?.();
      onOpenChange(false);
    } catch (e) {
      console.error('Error deleting task:', e);
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo eliminar la tarea',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'view' ? 'Detalle de la tarea' : 'Editar tarea'}</DialogTitle>
        </DialogHeader>

        {mode === 'view' ? (
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{task.titulo}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {formatDateRange(task.inicio, task.fin)}
              </p>
            </div>
            {task.notas && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Notas</p>
                <p className="text-sm whitespace-pre-line">{task.notas}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                Proveedores relacionados
              </p>
              {(task.providers ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                  No hay proveedores vinculados a esta tarea.
                </p>
              ) : (
                <div className="space-y-2">
                  {task.providers!.map((p) => (
                    <div key={p.evento_proveedor_id} className="border rounded-md p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">{p.tipo || 'Proveedor'}</Badge>
                        <span className="font-semibold text-sm">{p.nombre}</span>
                      </div>
                      {p.tel && (
                        <a
                          href={`tel:${p.tel}`}
                          className="flex items-center text-sm text-blue-700 hover:underline"
                        >
                          <Phone className="w-3.5 h-3.5 mr-2" />
                          {p.tel}
                        </a>
                      )}
                      {p.email && (
                        <a
                          href={`mailto:${p.email}`}
                          className="flex items-center text-sm text-blue-700 hover:underline break-all"
                        >
                          <Mail className="w-3.5 h-3.5 mr-2" />
                          {p.email}
                        </a>
                      )}
                      {p.descripcion_servicio && (
                        <p className="text-xs text-gray-600 pt-1">{p.descripcion_servicio}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-titulo">Título</Label>
              <Input
                id="edit-titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Fecha y hora inicio</Label>
                <DateTimePicker date={fechaInicio} setDate={setFechaInicio} />
              </div>
              <div>
                <Label>Fecha y hora fin (opcional)</Label>
                <DateTimePicker date={fechaFin} setDate={setFechaFin} />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-notas">Notas</Label>
              <Textarea
                id="edit-notas"
                rows={3}
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
              />
            </div>
            <div>
              <Label>Proveedores relacionados</Label>
              {eventProviders.length === 0 ? (
                <p className="text-xs text-gray-500 mt-1">
                  Aún no hay proveedores asignados a este evento.
                </p>
              ) : (
                <ProviderMultiSelect
                  options={eventProviders}
                  value={providerIds}
                  onChange={setProviderIds}
                />
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {mode === 'view' ? (
            <>
              {canWrite && (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleDelete}
                  disabled={submitting}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              )}
              <div className="flex-1" />
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cerrar
              </Button>
              {canWrite && (
                <Button onClick={() => setMode('edit')}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setMode('view')} disabled={submitting}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar cambios
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
